import { Link, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useApproveTask,
  useDeleteAttachment,
  useTask,
  useTaskAttachments,
  useTaskComments,
  useTaskEvents,
} from '../api/tasks';
import { useTaskChain } from '../api/chain';
import { useWebSocket } from '../api/ws';
import { STATUS_COLORS, STATUS_LABELS, type Task } from '../lib/types';
import PlanView from '../components/PlanView';
import EventsLog from '../components/EventsLog';
import CommentThread from '../components/CommentThread';
import CommentComposer from '../components/CommentComposer';
import ApprovalPanel from '../components/ApprovalPanel';
import ClarifyingQuestionsPanel from '../components/ClarifyingQuestionsPanel';
import TaskChain from '../components/TaskChain';
import TaskActions from '../components/TaskActions';
import ParentSummaryBanner from '../components/ParentSummaryBanner';
import RepoUrlPrompt from '../components/RepoUrlPrompt';
import BaseBranchPrompt from '../components/BaseBranchPrompt';
import {
  formatAbsoluteTime,
  formatDuration,
  formatRelativeTime,
  formatTokens,
} from '../lib/format';
import { cleanBranchName, gitHubCompareUrl } from '../lib/github';
import { useProject } from '../api/projects';

export default function TaskDetail() {
  const { slug, taskId } = useParams<{ slug: string; taskId: string }>();
  const { data: task } = useTask(taskId);
  const { data: events } = useTaskEvents(taskId);
  const { data: comments } = useTaskComments(taskId);
  const { data: chain } = useTaskChain(taskId);
  const { data: project } = useProject(slug);
  const qc = useQueryClient();
  const approve = useApproveTask(taskId!, slug!);

  // Live updates for this task.
  useWebSocket(`/ws/tasks/${taskId}`, (msg) => {
    if (msg.kind === 'task_event' || msg.kind === 'task_mutation') {
      qc.invalidateQueries({ queryKey: ['task', taskId] });
      qc.invalidateQueries({ queryKey: ['task-events', taskId] });
      qc.invalidateQueries({ queryKey: ['task-comments', taskId] });
      qc.invalidateQueries({ queryKey: ['task-chain', taskId] });
    }
  });

  if (!task) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto">
      <Link
        to={`/p/${slug}`}
        className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-emerald-700 mb-3"
      >
        <span aria-hidden>←</span> Back to {slug} board
      </Link>

      {chain && (
        <TaskChain
          ancestors={chain.ancestors}
          current={chain.current}
          descendants={chain.descendants}
          projectSlug={slug!}
        />
      )}

      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold mb-1">{task.title}</h1>
          <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
            <span className={`px-2 py-0.5 rounded border ${STATUS_COLORS[task.status]}`}>
              {STATUS_LABELS[task.status]}
            </span>
            {task.branch &&
              (() => {
                const displayBranch = cleanBranchName(task.branch);
                const baseBranch = project?.default_branch || 'main';
                const compareUrl = gitHubCompareUrl(
                  project?.repo_url,
                  baseBranch,
                  task.branch,
                );
                return (
                  <span className="font-mono inline-flex items-center gap-1">
                    branch:{' '}
                    {compareUrl ? (
                      <a
                        href={compareUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-700 hover:text-sky-900 hover:underline"
                        title={`Compare ${baseBranch}...${displayBranch} on GitHub`}
                      >
                        {displayBranch}
                      </a>
                    ) : (
                      <>
                        <span>{displayBranch}</span>
                        {project && !project.repo_url && (
                          <RepoUrlPrompt projectSlug={slug!} />
                        )}
                      </>
                    )}
                    {project && compareUrl && (
                      <BaseBranchPrompt
                        projectSlug={slug!}
                        currentBase={baseBranch}
                      />
                    )}
                  </span>
                );
              })()}
            {task.base_branch && !task.branch && (
              <span className="font-mono text-sky-700">
                requested branch: {task.base_branch}
              </span>
            )}
            {(() => {
              const isInFlight =
                task.status === 'running' ||
                task.status === 'queued' ||
                task.phase === 'planning' ||
                task.phase === 'executing';
              const duration = formatDuration(
                task.started_at ?? task.queued_at,
                isInFlight ? null : task.completed_at,
              );
              return (
                <>
                  <span
                    className="text-slate-600"
                    title={`Created ${formatAbsoluteTime(task.queued_at)}`}
                  >
                    created {formatRelativeTime(task.queued_at)}
                  </span>
                  {duration && (
                    <span
                      className="text-slate-600"
                      title={
                        isInFlight
                          ? `Started ${formatAbsoluteTime(task.started_at)} — live`
                          : `Started ${formatAbsoluteTime(task.started_at)}; finished ${formatAbsoluteTime(task.completed_at)}`
                      }
                    >
                      {isInFlight ? `running ${duration}` : `ran ${duration}`}
                    </span>
                  )}
                </>
              );
            })()}
            {task.pipeline_task_id && (
              <span className="font-mono">pipeline: {task.pipeline_task_id}</span>
            )}
            <span>
              tokens: {formatTokens(Number(task.total_input_tokens) || 0)} in /{' '}
              {formatTokens(Number(task.total_output_tokens) || 0)} out
            </span>
            {task.creator_name && (
              <span className="text-slate-600">created by <span className="font-medium">{task.creator_name}</span></span>
            )}
            {task.parent_task_id && (
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px]">
                follow-up
              </span>
            )}
            {task.risk_overall && (
              <span
                className={
                  'px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ' +
                  (task.risk_overall === 'high'
                    ? 'bg-red-100 text-red-800 border-red-300'
                    : task.risk_overall === 'medium'
                      ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-emerald-100 text-emerald-800 border-emerald-300')
                }
                title="Pipeline risk assessment — informational; every plan still requires approval"
              >
                risk: {task.risk_overall}
              </span>
            )}
          </div>
        </div>
        <TaskActions task={task} projectSlug={slug!} />
      </div>

      {/* Parent-context banner — only renders when this task is itself a
          sub-task. Direct parent gets the full summary; older ancestors
          are listed as breadcrumb pills so the chain stays visible. */}
      {task.parent_task_id && chain && chain.ancestors.length > 0 && (
        <ParentSummaryBanner
          parent={chain.ancestors[chain.ancestors.length - 1]!}
          olderAncestors={chain.ancestors.slice(0, -1)}
          projectSlug={slug!}
        />
      )}

      <div className="rounded border border-slate-200 bg-white p-4 mb-4">
        <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
          {task.parent_task_id ? 'This follow-up adds' : 'Original prompt'}
        </h3>
        <p className="text-sm whitespace-pre-wrap text-slate-800">{task.prompt}</p>
      </div>

      {(task.status === 'pending_questions' ||
        task.status === 'pending_questions_in_execution') && (
        <ClarifyingQuestionsPanel task={task} projectSlug={slug!} />
      )}

      {task.status === 'pending_approval' && (
        <ApprovalPanel
          task={task}
          projectSlug={slug!}
          onApprove={(approved, reason) => approve.mutate({ approved, reason })}
        />
      )}

      {task.status === 'failed' && (
        <div className="rounded-lg border-2 border-red-300 bg-red-50 p-4 mb-4">
          <h3 className="font-semibold text-red-900 mb-1">⚠ Task failed</h3>
          <p className="text-sm text-red-800 mb-2">
            {task.approved
              ? 'Execution stopped. Use Retry (top right) to resume from the last completed step — finished steps are committed and won’t re-run.'
              : 'Planning stopped before approval. Use Retry (top right) to re-plan from scratch.'}
          </p>
          {task.error && (
            <pre className="text-xs bg-white border border-red-200 rounded p-2 overflow-auto max-h-56 text-red-900 whitespace-pre-wrap">
              {task.error}
            </pre>
          )}
        </div>
      )}

      <AttachmentsPanel task={task} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          {task.plan_json ? (
            <PlanView plan={task.plan_json as Parameters<typeof PlanView>[0]['plan']} />
          ) : (
            <div className="rounded border border-slate-200 bg-white p-4 text-sm text-slate-500">
              Plan will appear here once the Architect produces it.
            </div>
          )}
        </div>
        <div>
          <EventsLog events={events ?? []} />
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-sm font-medium text-slate-700 mb-2">Comments</h2>
        <CommentThread comments={comments ?? []} projectSlug={slug} />
        <div className="mt-3">
          <CommentComposer task={task} projectSlug={slug!} />
        </div>
      </div>
    </div>
  );
}

/**
 * Shows the files the user attached at task-creation time. Delete is
 * only allowed before the worker starts execution — the prompt the
 * subprocess receives is baked at spawn time, so removing a file
 * after that wouldn't actually un-show it to the agents.
 */
function AttachmentsPanel({ task }: { task: Task }) {
  const attachments = useTaskAttachments(task.id);
  const del = useDeleteAttachment(task.id);
  const items = attachments.data ?? [];
  if (items.length === 0) return null;

  const canDelete = task.phase !== 'executing' && task.phase !== 'done';

  return (
    <div className="rounded border border-slate-200 bg-white p-4 mb-4">
      <h2 className="text-sm font-medium text-slate-700 mb-2">
        📎 Attachments <span className="text-slate-400 font-normal">({items.length})</span>
      </h2>
      <ul className="space-y-1">
        {items.map((a) => (
          <li
            key={a.id}
            className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1"
          >
            <a
              href={`/api/tasks/${task.id}/attachments/${a.id}`}
              target="_blank"
              rel="noreferrer"
              className="flex-1 truncate font-mono text-emerald-700 hover:underline"
              title={a.stored_path}
            >
              {a.filename}
            </a>
            <span className="text-xs text-slate-500 shrink-0">
              {formatBytes(a.size_bytes)}
              {a.mime_type ? ` · ${a.mime_type}` : ''}
            </span>
            {canDelete && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Remove ${a.filename}?`)) {
                    del.mutate(a.id);
                  }
                }}
                className="text-slate-400 hover:text-red-600"
                aria-label={`Remove ${a.filename}`}
                disabled={del.isPending}
              >
                ×
              </button>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-2 text-xs text-slate-500">
        Agents see these as <code>.pipeline/attachments/{task.id}/&lt;filename&gt;</code>
        {' '}and can Read them with their normal Read tool.
        {!canDelete && (
          <span className="block mt-1 text-slate-400">
            Files are locked once execution has begun — the prompt was
            captured at spawn time.
          </span>
        )}
      </p>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
